import { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import { useLocation, useParams } from 'react-router';
import { Row, Col } from 'react-grid-system';
import Page from 'components/layout/Page';
import { Flex, Box, Message, ClickableBox, Button } from 'components/ui';
import FuturaItemTraits from 'components/FuturaItemTraits';
import { getMixCodeFromAttributes } from 'utils/futura';
import { useWindowSize } from 'hooks/useWindowSize';
import { futuraChainId, futuraContractAddress } from 'utils/config';
import playIcon from 'img/play-icon.svg';
import stopIcon from 'img/stop-icon.svg';
import futuraSpec from 'utils/futuraSpec.json';
import { REQUEST_STATUS, FUTURA_ATTRIBUTES, GRID_BREAKPOINTS } from '../../constants';
import FuturaVisualization from 'components/FuturaVisualization';
import FuturaTokenLoading from 'components/FuturaTokenLoading';
import useInterval from 'react-useinterval';
import fullscreenIcon from 'img/fullscreen.svg';
import exitFullscreenIcon from 'img/exit-fullscreen.svg';
import previousArrowIcon from 'img/previous-arrow.svg';
import downloadIcon from 'img/download.svg';
import loadingSvg from 'img/loading-bw.svg';
import { Link } from 'react-router-dom';
import { useToken } from 'hooks/useEbApi';

// See this issue.
// https://github.com/vitejs/vite/issues/2139
import F from 'react-request-fullscreen';
const FullScreen = (F as any).default ? (F as any).default : F;

enum PreviewOptions {
  player = 'player',
  cover = 'cover',
}

const FuturaToken = () => {
  const { state } = useLocation<{ from?: Location }>();
  const [isDownloading, setIsDownloading] = useState(false);

  const EBF = (window as Window).EulerBeatsFutura;
  const { id } = useParams<any>();

  const { data: item, status } = useToken(
    futuraContractAddress,
    id ?? '',
    futuraChainId
  );

  const isRevealed = item?.metadata?.attributes[FUTURA_ATTRIBUTES.colorPalette];
  const title = item?.metadata?.name || 'Futura Token';
  const [preview, setPreview] = useState(PreviewOptions.player);
  const [playing, setPlaying] = useState(false);
  const elementId = `futura_${id}`;
  const ws = useWindowSize();

  const [isFullScreen, setIsFullScreen] = useState(false);
  const fullScreenRef: any = useRef();

  const onFullScreenChange = () => {
    setIsFullScreen(isFullScreen => !isFullScreen);
    EBF.visual.resize(elementId);
    setTimeout(() => {
      EBF.visual.resize(elementId);
    }, 1000);
  };

  const toggleFullScreen = async () => {
    fullScreenRef?.current?.fullScreen();
  };

  useEffect(() => {
    if (status === REQUEST_STATUS.success && !isRevealed) {
      setPreview(PreviewOptions.cover);
    }
  }, [status, isRevealed]);

  const tempo = item?.metadata?.attributes[FUTURA_ATTRIBUTES.bpm];
  const pieceSpec = useMemo(
    () => (isRevealed ? EBF.utils.updatePieceSpecRaw(futuraSpec) : null),
    [isRevealed, EBF]
  );
  const pieceData = useMemo(
    () =>
      isRevealed
        ? EBF.utils.buildPieceData({
            pieceSpec,
            mixCode: getMixCodeFromAttributes(item?.metadata?.attributes),
            tempo,
          })
        : null,
    [isRevealed, item, EBF.utils, pieceSpec, tempo]
  );

  const togglePlay = useCallback(() => {
    EBF.visual.toggle(elementId);
  }, [EBF.visual, elementId]);

  const downloadAudio = useCallback(async () => {
    setIsDownloading(true)
    await EBF.visual.download(elementId, { name: item?.metadata?.name, kbps: 0 }).catch(console.error);
    setIsDownloading(false);
  }, [EBF.visual, elementId, item?.metadata?.name]);

  useInterval(() => {
    setPlaying(EBF.visual.isPlaying(elementId));
  }, 500);

  let size = 480;
  if (ws.width < GRID_BREAKPOINTS.sm) {
    size = ws.width - 40;
  } else if (ws.width < GRID_BREAKPOINTS.md) {
    size = ws.width * 0.5 - 80;
  } else if (ws.width < GRID_BREAKPOINTS.xxl) {
    size = ws.width * 0.35 - 80;
  }
  let width = size;
  let height = size;
  if (isFullScreen) {
    width = ws.width - 40;
    height = ws.height - 100;
  }

  const leftSectionHeight = ws.width < GRID_BREAKPOINTS.sm ? 'auto' : '1048px';

  return (
    <FullScreen
      ref={fullScreenRef}
      onFullScreenChange={onFullScreenChange}
      onFullScreenError={error => console.error(error)}
    >
      <Page
        title={title}
        padding="0"
        background="#333"
        showHeader={!isFullScreen}
        showFooter={!isFullScreen}
        fixedFooter={false}
      >
        {status === REQUEST_STATUS.loading && (
          <FuturaTokenLoading size={size} leftSectionHeight={leftSectionHeight} />
        )}

        {status === REQUEST_STATUS.error && (
          <Message width="100%" maxWidth="320px" margin="2em auto" color="black">
            Error loading item
          </Message>
        )}

        {status === REQUEST_STATUS.success && (
          <Row>
            <Col xs={12} sm={isFullScreen ? 12 : 6} md={isFullScreen ? 12 : 5}>
              {!isFullScreen && (
                <Flex
                  justifyContent="center"
                  backgroundColor="black"
                  paddingTop={4}
                  paddingLeft={1}
                >
                  <Link to={state?.from ?? '/futura/gallery'}>
                    <Button
                      boxShadow="0"
                      height={30}
                      border={1}
                      borderColor="#303030"
                      width={180}
                      borderRadius={4}
                      paddingX={3}
                      background="#191919"
                    >
                      <Flex>
                        <img
                          style={{ padding: '0 10px 0 0' }}
                          src={previousArrowIcon}
                          height={20}
                          alt="Previous"
                        />
                        <Box borderLeft={1} paddingLeft={2} color="white">
                          Back to gallery
                        </Box>
                      </Flex>
                    </Button>
                  </Link>
                </Flex>
              )}

              <Box
                width="100%"
                backgroundColor="black"
                height={leftSectionHeight}
                padding={['24px 8px', '24px']}
              >
                <Box display={preview === PreviewOptions.player ? 'block' : 'none'}>
                  {item !== undefined && isRevealed && (
                    <FuturaVisualization
                      elementId={elementId}
                      width={width}
                      height={height}
                      pieceData={pieceData}
                      twists={item?.metadata?.attributes[FUTURA_ATTRIBUTES.twists]}
                      distortion={
                        item?.metadata?.attributes[FUTURA_ATTRIBUTES.distortion] === 'true'
                      }
                      paletteName={item?.metadata?.attributes[FUTURA_ATTRIBUTES.colorPalette]}
                      noteShape={item?.metadata?.attributes[FUTURA_ATTRIBUTES.noteShape]}
                    />
                  )}
                </Box>

                {item?.metadata?.image && (
                  <Box
                    width={width}
                    height={height}
                    margin="0 auto"
                    display={preview === PreviewOptions.cover ? 'block' : 'none'}
                  >
                    <img
                      src={item?.metadata?.image}
                      alt={item?.metadata?.name}
                      width={width}
                      height={height}
                    />
                  </Box>
                )}

                {isRevealed && (
                  <Flex
                    margin="16px auto 0"
                    width={width}
                    position="relative"
                    justifyBetween
                    alignCenter
                  >
                    <Flex overflow="hidden" borderRadius="8px">
                      <ClickableBox
                        px={[1, 3]}
                        py={[1, 2]}
                        fontSize={['14px', 'auto']}
                        onClick={() => setPreview(PreviewOptions.player)}
                        backgroundColor={preview === PreviewOptions.player ? '#222' : '#333'}
                      >
                        Player
                      </ClickableBox>
                      <ClickableBox
                        px={[1, 3]}
                        py={[1, 2]}
                        fontSize={['14px', 'auto']}
                        onClick={() => setPreview(PreviewOptions.cover)}
                        backgroundColor={preview === PreviewOptions.cover ? '#222' : '#333'}
                      >
                        Cover
                      </ClickableBox>
                    </Flex>

                    <ClickableBox
                      position="absolute"
                      left="50%"
                      marginLeft="-20px"
                      onClick={togglePlay}
                    >
                      {playing ? (
                        <img src={stopIcon} alt="play" width="40px" />
                      ) : (
                        <img src={playIcon} alt="play" width="40px" />
                      )}
                    </ClickableBox>

                    {isRevealed && !isFullScreen && !isDownloading && (
                      <ClickableBox onClick={downloadAudio}>
                        <img src={downloadIcon} width="24px" alt="download" />
                      </ClickableBox>
                    )}

                    {isDownloading && (
                      <Box position="relative" width="40px" height="40px">
                        <Box position="absolute" top="-4px">
                          <img src={loadingSvg} width="44" alt="Loading" />
                        </Box>
                      </Box>
                    )}

                    {isRevealed && (
                      <ClickableBox onClick={toggleFullScreen}>
                        <img
                          src={isFullScreen ? exitFullscreenIcon : fullscreenIcon}
                          width="24px"
                          alt="toggle Fullscreen"
                        />
                      </ClickableBox>
                    )}
                  </Flex>
                )}

                {isDownloading && (
                  <Box width={width} margin="0 auto" mt={4} fontSize="14px">
                    Preparing download...
                  </Box>
                )}

                <Box width={width} margin="0 auto" mt={4} color="white" fontSize="14px">
                  Drag and drop to rotate. Press 'C' to change camera modes while playing.
                </Box>
              </Box>
            </Col>

            {!isFullScreen && item && id && (
              <Col xs={12} sm={6} md={7}>
                <Box padding={['24px 8px', '24px']}>
                  <FuturaItemTraits item={item} pieceData={pieceData} tokenId={id} />
                </Box>
              </Col>
            )}
          </Row>
        )}
      </Page>
    </FullScreen>
  );
};

export default FuturaToken;
